import { Moment } from 'moment'
import { CustomFieldDataType, CustomListItemStyle } from '@cims-online/shared'
import {
  MutateListCustomFieldValueInput,
  MutateListCustomFieldValuesInput,
  MutateBooleanListCustomFieldValuesInput,
  MutateCustomFieldsInput,
} from '../../../../schema'
import { SEPARATOR } from '../../../CIMSSelect/common/types'
import { MetadataListFormItemsValue } from '../../CustomFieldEditor/CustomFieldRow/MetadataListFormItems/types'
import {
  SimpleCustomFieldEditorValue,
  SelectCustomFieldEditorValue,
  SelectCustomFieldEditorValues,
} from '../../CustomFieldEditor/types'
import { ProjectCustomFieldsContext } from '../types'

/**
 * @see {@link ProjectCustomFieldsContext.toMutateCustomFieldsInput}
 */
export const toMutateCustomFieldsInput: ProjectCustomFieldsContext['toMutateCustomFieldsInput'] =
  (customFields) => {
    if (!customFields) return undefined

    const singles: MutateListCustomFieldValueInput[] = []
    const multiples: MutateListCustomFieldValuesInput[] = []
    const checkboxes: MutateBooleanListCustomFieldValuesInput[] = []
    const input = Object.keys(customFields).reduce<MutateCustomFieldsInput>(
      (input, customFieldId) => {
        // we can use the dataTypeId to "safely" cast the value
        const customField = customFields[customFieldId]
        if (customField.dataTypeId === CustomFieldDataType.Text.id)
          input.textInputs = orNew(input.textInputs, {
            customFieldId,
            value: (customField as SimpleCustomFieldEditorValue)
              .value as string,
          })
        else if (customField.dataTypeId === CustomFieldDataType.Date.id)
          input.dateInputs = orNew(input.dateInputs, {
            customFieldId,
            value: (
              (customField as SimpleCustomFieldEditorValue).value as Moment
            )?.toISOString(),
          })
        else if (customField.dataTypeId === CustomFieldDataType.Number.id)
          input.numberInputs = orNew(input.numberInputs, {
            customFieldId,
            value: (customField as SimpleCustomFieldEditorValue)
              .value as number,
          })
        else if (customField.dataTypeId === CustomFieldDataType.Entity.id)
          input.entityInputs = orNew(input.entityInputs, {
            customFieldId,
            entities: resolveMetadataListFormItemsValue(
              customField as MetadataListFormItemsValue
            ).map(({ typeId, value }) => ({
              entityId: typeId,
              recordId: value,
            })),
          })
        else if (customField.dataTypeId === CustomFieldDataType.Report.id)
          input.reportInputs = orNew(input.reportInputs, {
            customFieldId,
            reports: resolveMetadataListFormItemsValue(
              customField as MetadataListFormItemsValue
            ).map(({ typeId, value }) => ({
              reportId: typeId,
              recordId: value?.split(SEPARATOR)[1],
            })),
          })
        else if (customField.dataTypeId === CustomFieldDataType.List.id) {
          const { styleId } = customField as
            | SimpleCustomFieldEditorValue
            | SelectCustomFieldEditorValue
          if (styleId === CustomListItemStyle.Multiple.id)
            multiples.push({
              customFieldId,
              customListItemIds:
                (customField as SelectCustomFieldEditorValues)?.value?.map(
                  ({ value }) => value as string
                ) ?? [],
            })
          else if (styleId === CustomListItemStyle.Checkboxes.id)
            checkboxes.push({
              customFieldId,
              booleans:
                (
                  (customField as SimpleCustomFieldEditorValue)
                    .value as string[]
                )?.map((customListItemId) => ({
                  customListItemId,
                  checked: true,
                })) ?? [],
            })
          else
            singles.push({
              customFieldId,
              customListItemId: (customField as SelectCustomFieldEditorValue)
                ?.value?.value as string,
            })
        } else if (customField.dataTypeId === CustomFieldDataType.Boolean.id)
          input.booleanInputs = orNew(input.booleanInputs, {
            customFieldId,
            value: (customField as SimpleCustomFieldEditorValue)
              .value as boolean,
          })
        return input
      },
      Object()
    )

    if (!!singles.length || !!multiples.length || !!checkboxes.length)
      input.listInputs = {
        singles: !!singles.length ? singles : undefined,
        multiples: !!multiples.length ? multiples : undefined,
        checkboxes: !!checkboxes.length ? checkboxes : undefined,
      }
    return input
  }

function orNew<T>(items: T[] | undefined | null, item: T) {
  if (items) return [...items, item]
  return [item]
}

function resolveMetadataListFormItemsValue({
  entities,
  reports,
}: MetadataListFormItemsValue) {
  const resolved: { typeId: string; value: string | undefined }[] = []
  const value = entities ?? reports
  if (!value) return resolved
  Object.keys(value).forEach((key) => {
    resolved.push({ typeId: key, value: value[key]?.value as string })
  })
  return resolved
}
